import { Map, List, fromJS } from 'immutable';
import * as types from 'constants/ActionTypes';
import findTrip from 'utils/trips/findTrip';

const tripTemplate = (active) => Map({ isPolling: active, trips: List() });

export default function trips(trips = Map({}), action) {
  let requestInfo;

  switch (action.type) {
    case types.SETUP_TRIPS:
      return trips.mergeIn(
        [action.searchId],
        Map({
          buses: tripTemplate(action.activeBuses),
          flights: tripTemplate(action.activeFlights),
          mixed: tripTemplate(action.activeMixed),
        }),
      );

    case types.RECEIVE_BUSES: {
      requestInfo = Map(action.apiResponse)
        .delete('trips')
        .delete('closedTrips')
        .delete('afterMidnight');
      const searchId = requestInfo.get('id');
      const mapedTrips = action.apiResponse.trips.map((trip) => {
        const existentTrip = findTrip(trips.get(searchId), 'bus', trip.id);
        return {
          ...trip,
          isLoading: existentTrip ? existentTrip.get('isLoading') === true : false,
        };
      });

      const closedTrips = action.apiResponse.closedTrips || [];

      // Retain the last after-midnight section that carried trips.
      //
      // `merge` is shallow in immutable 3.8, so `buses` is replaced wholesale from the current
      // payload: a key absent from a later poll would simply vanish. And the payload does thin out
      // on its own once the next-day search passes its cache tolerance, so this is the normal path,
      // not a precaution.
      //
      // Retention lives here keyed by searchId, never in module scope: SETUP_TRIPS replaces `buses`
      // for its searchId, so a new search starts clean. A module-level cache would survive that and
      // show last night's small hours on today's search.
      //
      // Retained or replaced whole, never half of each: mixing kept trips into a newer section
      // would compose something no payload ever carried.
      //
      // The decision itself is not taken here — `actions/search.js` already made it, where the
      // payload still tells a present-but-empty section apart from an absent one. This branch only
      // reads the verdict: a section to store, an explicit `null` to drop it, or no key at all,
      // which means keep what is on screen.
      const incomingAfterMidnight = action.apiResponse.afterMidnight;
      const dropsAfterMidnight = 'afterMidnight' in action.apiResponse && !incomingAfterMidnight;
      let afterMidnight = null;

      if (incomingAfterMidnight) afterMidnight = fromJS(incomingAfterMidnight);
      else if (!dropsAfterMidnight)
        afterMidnight = trips.getIn([searchId, 'buses', 'afterMidnight']);

      let busesInfo = requestInfo.merge({
        isPolling: action.isPolling,
        trips: fromJS(mapedTrips),
        closedTrips: fromJS(closedTrips),
      });

      // Written only when there is something to write, so a search that never carries the key
      // leaves the state exactly as it was before this capability existed.
      if (afterMidnight) busesInfo = busesInfo.set('afterMidnight', afterMidnight);

      return trips.mergeIn([searchId], Map({ buses: busesInfo }));
    }

    case types.RECEIVE_FLIGHTS:
      requestInfo = Map(action.apiResponse).delete('flights');

      return trips.mergeIn(
        [requestInfo.get('id')],
        Map({
          flights: requestInfo.merge({
            isPolling: action.isPolling,
            trips: fromJS(action.apiResponse.flights),
          }),
        }),
      );

    case types.RECEIVE_MIXED:
      requestInfo = Map(action.apiResponse).delete('itineraries');

      return trips.mergeIn(
        [requestInfo.get('id')],
        Map({
          mixed: requestInfo.merge({
            isPolling: action.isPolling,
            trips: fromJS(action.apiResponse.itineraries),
          }),
        }),
      );
    default:
      return trips;
  }
}
